import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { scholarshipsAPI, fundingAppliedAPI, fundingSavedAPI } from '../services/api';
import { toast } from 'sonner';
import TopExperienceLayer from '../components/top/TopExperienceLayer';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Search, Bookmark, DollarSign, ExternalLink } from 'lucide-react';
import SelectField from '../components/ui/SelectField';
import { formatDate } from '../utils/formatters';
import { normalizeEligibility } from '../utils/eligibilityNormalizer';
import AuthorityPreviewPanel from '../components/funding/AuthorityPreviewPanel';

// Filter Engine imports
import {
  DEFAULT_FILTERS,
  DEADLINE_WINDOWS,
  EDUCATION_LEVELS,
  ETHNICITY_STATUS_OPTIONS,
  FUNDING_TYPES,
  applyFilters,
  getActiveFilterChips,
  queryParamsToFilters,
  filtersToQueryParams,
  hasActiveFilters,
} from '../utils/filterEngine';
import ActiveFilterChips from '../components/filters/ActiveFilterChips';
import AmountRangeFilter from '../components/filters/AmountRangeFilter';
import ZeroResultsMessage from '../components/filters/ZeroResultsMessage';
import MultiSelectDropdown from '../components/filters/MultiSelectDropdown';

// Generate or retrieve session ID for anonymous users
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem('session_id', sessionId);
  }
  return sessionId;
};

const ScholarshipsPage = () => {
  const _navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  
  // Data state
  const [scholarships, setScholarships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savedFunding, setSavedFunding] = useState([]);
  const [appliedMap, setAppliedMap] = useState({});
  
  // Filter state - derived from URL (single source of truth per doctrine)
  const [filters, setFilters] = useState(() => queryParamsToFilters(searchParams));
  
  // UI state
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  // Sync URL params with filter state (bidirectional)
  useEffect(() => {
    const urlFilters = queryParamsToFilters(searchParams);
    setFilters(urlFilters);
  }, [searchParams]);

  // Update URL when filters change
  const updateFilters = useCallback((newFilterValues) => {
    setFilters(prev => {
      const updated = { ...prev, ...newFilterValues };
      // Update URL params
      const params = filtersToQueryParams(updated);
      setSearchParams(params, { replace: true });
      return updated;
    });
  }, [setSearchParams]);

  // Clear all filters (per doctrine: true global reset)
  const clearAllFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  // Remove individual filter chip
  const removeFilterChip = useCallback((chip) => {
    // Handle ethnicity/status chips specially
    if (chip.isEthnicityStatus) {
      const { ethnicityValue } = chip;
      const currentValues = filters.ethnicityStatus || [];
      const newValues = currentValues.filter(v => v !== ethnicityValue);
      updateFilters({ ethnicityStatus: newValues });
      return;
    }
    
    // Handle standard filter chips
    const updates = {};
    chip.removeKeys.forEach(key => {
      if (chip.resetValue !== undefined) {
        updates[key] = chip.resetValue;
      } else if (key === 'educationLevels' || key === 'ethnicityStatus') {
        updates[key] = [];
      } else if (key === 'searchQuery' || key === 'deadline') {
        updates[key] = '';
      } else {
        updates[key] = null;
      }
    });
    updateFilters(updates);
  }, [updateFilters, filters.ethnicityStatus]);

  // Update ethnicity/status filter
  const updateEthnicityStatus = useCallback((values) => {
    updateFilters({ ethnicityStatus: values });
  }, [updateFilters]);

  // Apply filters and get results with metadata
  const { results: filteredScholarships, metadata: filterMetadata } = useMemo(() => {
    return applyFilters(scholarships, filters);
  }, [scholarships, filters]);

  // Get active filter chips for display
  const activeChips = useMemo(() => {
    return getActiveFilterChips(filters);
  }, [filters]);

  // Fetch applied status
  const fetchAppliedStatus = useCallback(async () => {
    try {
      const sessionId = getSessionId();
      const userId = isAuthenticated ? user?.id : null;
      const applied = await fundingAppliedAPI.getApplied(userId, sessionId);
      setAppliedMap(applied);
    } catch (err) {
      console.log('Error fetching applied status:', err);
    }
  }, [isAuthenticated, user?.id]);

  // Fetch saved funding (only if authenticated)
  const fetchSavedFunding = useCallback(async () => {
    if (!isAuthenticated) {
      setSavedFunding([]);
      return;
    }
    try {
      const saved = await fundingSavedAPI.getSaved();
      setSavedFunding(saved);
    } catch (err) {
      console.log('Error fetching saved funding:', err);
    }
  }, [isAuthenticated]);

  // Fetch scholarships from backend
  useEffect(() => {
    const fetchScholarships = async () => {
      try {
        setLoading(true);
        const response = await scholarshipsAPI.getScholarships();
        setScholarships(response.scholarships || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching scholarships:', err);
        setError('Failed to load scholarships. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchScholarships();
  }, []);

  // Fetch applied status on mount and when auth changes
  useEffect(() => {
    fetchAppliedStatus();
  }, [fetchAppliedStatus]);

  // Fetch user's saved funding on mount and when auth changes
  useEffect(() => {
    fetchSavedFunding();
  }, [fetchSavedFunding]);

  const toggleSaveFunding = async (fundingId) => {
    if (!isAuthenticated) {
      const modal = document.createElement('div');
      modal.style.cssText = 'position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); background: white; padding: 30px; border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.3); z-index: 10000; max-width: 400px; width: 90%;';
      modal.innerHTML = `
        <h3 style="font-size: 22px; font-weight: bold; color: #f5a623; margin-bottom: 16px;">Log In Required</h3>
        <p style="color: #4a5568; line-height: 1.6; margin-bottom: 20px;">Please log in to save scholarships to your list.</p>
        <div style="display: flex; gap: 10px;">
          <button onclick="window.location.href='/login'" style="flex: 1; background: #f5a623; color: white; padding: 12px 24px; border: none; border-radius: 6px; font-weight: bold; cursor: pointer;">Log In</button>
          <button onclick="this.parentElement.parentElement.remove(); document.getElementById('modal-overlay').remove();" style="flex: 1; background: #e5e7eb; color: #374151; padding: 12px 24px; border: none; border-radius: 6px; font-weight: bold; cursor: pointer;">Cancel</button>
        </div>
      `;
      
      const overlay = document.createElement('div');
      overlay.id = 'modal-overlay';
      overlay.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 9999;';
      overlay.onclick = () => { modal.remove(); overlay.remove(); };
      
      document.body.appendChild(overlay);
      document.body.appendChild(modal);
      return;
    }

    try {
      const isSaved = savedFunding.includes(fundingId);
      
      if (isSaved) {
        await fundingSavedAPI.unsave(fundingId);
        setSavedFunding(prev => prev.filter(id => id !== fundingId));
        toast.success('Removed from saved');
      } else {
        await fundingSavedAPI.save(fundingId);
        setSavedFunding(prev => [...prev, fundingId]);
        toast.success('Saved for later');
      }
    } catch (error) {
      console.error('Error saving/unsaving funding:', error);
      toast.error('Failed to update saved status. Please try again.');
    }
  };

  // Panel handlers
  const openPreviewPanel = (scholarship) => {
    setSelectedOpportunity(scholarship);
    setIsPanelOpen(true);
  };

  const closePreviewPanel = () => {
    setIsPanelOpen(false);
    setSelectedOpportunity(null);
  };

  const handleApplied = (fundingId, timestamp) => {
    setAppliedMap(prev => ({
      ...prev,
      [fundingId]: timestamp
    }));
  };

  const handleLearnMore = (scholarship) => {
    openPreviewPanel(scholarship);
  };

  const handleStartApplying = (scholarship) => {
    openPreviewPanel(scholarship);
  };

  // Handle education level toggle
  const toggleEducationLevel = (level) => {
    const currentLevels = filters.educationLevels || [];
    const newLevels = currentLevels.includes(level)
      ? currentLevels.filter(l => l !== level)
      : [...currentLevels, level];
    updateFilters({ educationLevels: newLevels });
  };

  // NOTE: handleRenewableChange removed in v1.1 - renewable is now display-only metadata

  return (
    <div className="min-h-screen flex flex-col">
      <TopExperienceLayer />
      
      {/* Hero Section with Search */}
      <section className="relative py-16" style={{ backgroundColor: '#1a5d3a' }}>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
              Browse Scholarships for College Students
            </h1>
            <p className="text-lg text-white/90 max-w-3xl mx-auto">
              The nation&apos;s largest scholarship database. Create a free account to see all of your 
              personalized matches and start applying today!
            </p>
          </div>
          
          {/* Search Bar */}
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={24} />
              <input
                type="text"
                placeholder="Search scholarships..."
                value={filters.searchQuery}
                onChange={(e) => updateFilters({ searchQuery: e.target.value })}
                className="w-full pl-14 pr-4 py-4 text-lg border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#1a5d3a] focus:border-[#1a5d3a] bg-white"
              />
            </div>
          </div>
        </div>
      </section>

      <main className="flex-1 bg-gray-50">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex gap-6">
            {/* Sidebar Filters */}
            <aside className="hidden lg:block w-72 flex-shrink-0">
              <div className="bg-white rounded-md shadow-md border border-gray-200 p-5 sticky top-24">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-semibold text-gray-900">Filters</h3>
                  {hasActiveFilters(filters) && (
                    <button
                      onClick={clearAllFilters}
                      className="text-sm text-[#1a5d3a] hover:underline"
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {/* === FILTER ORDER: Amount → Deadline → Funding Type → Ethnicity/Status → Education === */}
                
                {/* 1. Award Amount */}
                <div className="mb-6 pb-6 border-b border-gray-200">
                  <AmountRangeFilter
                    minValue={filters.amountMin}
                    maxValue={filters.amountMax}
                    onChange={updateFilters}
                  />
                </div>

                {/* 2. Application Deadline */}
                <div className="mb-6 pb-6 border-b border-gray-200">
                  <SelectField
                    label="Application Deadline"
                    value={filters.deadline}
                    onChange={(value) => updateFilters({ deadline: value })}
                    options={DEADLINE_WINDOWS.map(w => ({ value: w.value, label: w.label }))}
                    placeholder="Any deadline"
                  />
                </div>

                {/* 3. Funding Type */}
                <div className="mb-6 pb-6 border-b border-gray-200">
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Funding Type
                  </label>
                  <div className="space-y-2">
                    {FUNDING_TYPES.map((type) => (
                      <label key={type.value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="fundingType"
                          checked={filters.fundingType === type.value}
                          onChange={() => updateFilters({ fundingType: type.value })}
                          className="w-4 h-4 text-[#1a5d3a] border-gray-300 focus:ring-[#1a5d3a]"
                        />
                        <span className="text-sm text-gray-700">{type.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* 4. Ethnicity / Status */}
                <div className="mb-6 pb-6 border-b border-gray-200">
                  <MultiSelectDropdown
                    label="Ethnicity / Status"
                    options={ETHNICITY_STATUS_OPTIONS}
                    selectedValues={filters.ethnicityStatus || []}
                    onChange={updateEthnicityStatus}
                    placeholder="Any"
                  />
                </div>

                {/* 5. Education Level */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Education Level
                  </label>
                  <div className="space-y-2">
                    {EDUCATION_LEVELS.map((level) => (
                      <label key={level.value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={(filters.educationLevels || []).includes(level.value)}
                          onChange={() => toggleEducationLevel(level.value)}
                          className="w-4 h-4 text-[#1a5d3a] border-gray-300 rounded focus:ring-[#1a5d3a]"
                        />
                        <span className="text-sm text-gray-700">{level.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1">
              {/* Loading State */}
              {loading && (
                <div className="flex justify-center items-center py-20">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#f5a623]"></div>
                </div>
              )}

              {/* Error State */}
              {error && !loading && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                  <p className="text-red-800 font-semibold mb-2">Error Loading Scholarships</p>
                  <p className="text-red-600">{error}</p>
                  <button 
                    onClick={() => window.location.reload()}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    Retry
                  </button>
                </div>
              )}

              {/* Content */}
              {!loading && !error && (
                <>
                  {/* Active Filter Chips */}
                  <ActiveFilterChips 
                    chips={activeChips}
                    onRemove={removeFilterChip}
                    onClearAll={clearAllFilters}
                  />

                  {/* Results Count */}
                  <div className="mb-5 bg-white px-4 py-3 rounded-md shadow-sm border border-gray-200">
                    <p className="text-sm font-medium text-gray-700">
                      {filterMetadata.filtered === filterMetadata.total ? (
                        <>Showing all <strong>{filterMetadata.total}</strong> scholarships</>
                      ) : (
                        <>
                          Showing <strong>{filterMetadata.filtered}</strong> of {filterMetadata.total} scholarships
                          {filterMetadata.excluded > 0 && (
                            <span className="text-gray-500 ml-1">
                              ({filterMetadata.excluded} filtered out)
                            </span>
                          )}
                        </>
                      )}
                    </p>
                  </div>

                  {/* Zero Results Message */}
                  {filteredScholarships.length === 0 && filterMetadata.total > 0 && (
                    <ZeroResultsMessage 
                      metadata={filterMetadata}
                      onClearFilters={clearAllFilters}
                    />
                  )}

                  {/* Scholarship Cards Grid */}
                  {filteredScholarships.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {filteredScholarships.map((scholarship) => (
                        <div
                          key={scholarship.id}
                          className="bg-white rounded-md shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-200 flex flex-col"
                        >
                          {/* Award Amount Header */}
                          <div className="bg-gradient-to-br from-[#f5a623]/10 to-[#f5a623]/20 p-4 border-b border-[#f5a623]/20">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <DollarSign className="text-[#f5a623]" size={24} />
                                <div className="text-2xl font-bold text-[#1a5d3a]">
                                  {scholarship.amount?.replace('Up to ', '') || 'Varies'}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {/* Visited Badge */}
                                {appliedMap[scholarship.id] && (
                                  <span 
                                    className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold"
                                    title="You visited the official application site"
                                  >
                                    <ExternalLink size={12} />
                                    Visited
                                  </span>
                                )}
                                <button 
                                  onClick={() => toggleSaveFunding(scholarship.id)}
                                  className={`transition-colors ${savedFunding.includes(scholarship.id) ? 'text-[#f5a623]' : 'text-gray-400 hover:text-[#f5a623]'}`}
                                  title={savedFunding.includes(scholarship.id) ? 'Saved' : 'Save for later'}
                                >
                                  <Bookmark size={20} fill={savedFunding.includes(scholarship.id) ? '#f5a623' : 'none'} />
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Scholarship Details */}
                          <div className="p-4 flex flex-col flex-1">
                            <h3 className="text-lg font-bold text-gray-900 hover:text-[#1a5d3a] cursor-pointer mb-2 line-clamp-2">
                              {scholarship.name}
                            </h3>
                            
                            <p className="text-sm text-gray-600 mb-4 leading-relaxed line-clamp-3">
                              {normalizeEligibility(scholarship.description)}
                            </p>
                            
                            <div className="space-y-2 mb-4 text-sm">
                              <div className="flex items-start gap-2">
                                <span className="font-semibold text-gray-700 min-w-[70px]">Deadline:</span>
                                <span className="text-gray-600">{formatDate(scholarship.deadline)}</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <span className="font-semibold text-gray-700 min-w-[70px]">Type:</span>
                                <span className="text-gray-600">{scholarship.type}</span>
                              </div>
                            </div>

                            <div className="mb-4">
                              <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                                scholarship.renewable 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {scholarship.renewable ? 'Renewable Award' : 'One Time Award'}
                              </span>
                            </div>

                            <div className="mt-auto">
                              <div className="flex gap-2">
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleLearnMore(scholarship);
                                  }}
                                  className="flex-1 text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-md font-medium text-sm py-2 px-4 transition-colors cursor-pointer"
                                >
                                  Learn More
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleStartApplying(scholarship);
                                  }}
                                  className="flex-1 bg-[#f5a623] hover:bg-[#e09000] text-white rounded-md font-semibold text-sm py-2 px-4 transition-colors cursor-pointer"
                                >
                                  Start Applying
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Information Section */}
                  <div className="mt-12 bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">What are Scholarships for College Students?</h2>
                    <p className="text-gray-700 mb-6">
                      Scholarships are a smart and convenient way to pay for your college education because they are money earned that you don&apos;t have to pay back. Scholarships can be awarded for various things, including just for attending college.
                    </p>
                    <p className="text-gray-700 mb-6">
                      Scholarships for college students will often require you to submit an essay, report card, or other proof of enrollment at a college. Some scholarships must be sent directly to your institution and will be used to cover tuition costs. Others are dispersed to you for your discretionary use and covering expenses.
                    </p>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-3 mt-8">How Do I Qualify for Scholarships for College Students?</h3>
                    <p className="text-gray-700 mb-6">
                      In general, if you&apos;re a student who is attending college or going to be attending college, you may be eligible for college student scholarships. All scholarships will have different requirements because different organizations, businesses, or people offer them.
                    </p>

                    <h3 className="text-xl font-bold text-gray-900 mb-3 mt-8">What Types of Scholarships Are There?</h3>
                    <p className="text-gray-700 mb-4">
                      There are countless different types of scholarship opportunities for college students. Some common types of scholarship categories include:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                      <li>Merit-based scholarships for college students</li>
                      <li>First-generation college student scholarships</li>
                      <li>College scholarships for Hispanic students</li>
                      <li>Scholarships for current college students</li>
                      <li>College scholarships for international students</li>
                    </ul>

                    <h3 className="text-xl font-bold text-gray-900 mb-3 mt-8">Let Student Signal Help You Find Scholarships</h3>
                    <p className="text-gray-700">
                      Finding college student scholarships can be a lengthy process. You can start today by searching our extensive scholarship database and applying to as many scholarships as possible to build your financial aid package.
                    </p>
                  </div>

                  {/* CTA */}
                  <div className="mt-8 bg-[#1a5d3a] rounded-lg p-8 text-center text-white">
                    <h3 className="text-2xl font-bold mb-4">Create a free account to see all your scholarship matches</h3>
                    <Button size="lg" className="bg-[#f5a623] hover:bg-[#e09000] text-white font-semibold px-8">
                      Get Started
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Authority Preview Panel */}
      <AuthorityPreviewPanel
        opportunity={selectedOpportunity}
        isOpen={isPanelOpen}
        onClose={closePreviewPanel}
        onSave={toggleSaveFunding}
        isSaved={selectedOpportunity ? savedFunding.includes(selectedOpportunity.id) : false}
        appliedTimestamp={selectedOpportunity ? appliedMap[selectedOpportunity.id] : null}
        onApplied={handleApplied}
      />
    </div>
  );
};

export default ScholarshipsPage;
